<template>
  <ContentblockCube
    v-if="useCubeVariant"
    :content="props.content"
    :resolved-slides="slides"
    :bread-crumb="props.breadCrumb"
    :settings="props.settings"
    :contentblock-idx="props.contentblockIdx"
  />
  <div
    v-else
    class="hero-slider no-margin-top no-margin-bottom"
  >
    <ClientOnly>
      <Swiper
        v-bind="swiperOptions"
        class="hero-slider-swiper"
        @swiper="onSwiper"
      >
        <SwiperSlide
          v-for="(slide, index) in slides"
          :key="slide._id"
        >
          <HeroSliderSlide
            :content="slide.content"
            :event="slide.event"
            :loading="index === 0 ? 'eager' : 'lazy'"
          />
        </SwiperSlide>
        <div class="swiper-pagination" />
      </Swiper>

      <template #fallback>
        <HeroSliderSlide
          v-if="slides[0]"
          :content="slides[0].content"
          loading="eager"
        />
      </template>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
  import type { ElocusPage, BreadCrumb } from '@tc8l/nuxt-base'
  import type { HeroContent } from '@/types/contentblocks'
  import type {
    HeroSlideResolvedEvent,
    HeroSlideItem,
    HeroSlideSource,
    HeroSlidePageResponse,
  } from '@/types/heroSlider'
  import { SLIDES_PAGE_ID } from '~/config/elocusConfig'
  import type { Swiper as SwiperClass } from 'swiper/types'
  import { Swiper, SwiperSlide } from 'swiper/vue'
  import { Pagination, EffectCreative, Parallax } from 'swiper/modules'

  import 'swiper/css'
  import 'swiper/css/pagination'
  import 'swiper/css/effect-creative'
  import 'swiper/css/parallax'

  const props = defineProps<{
    content: HeroContent
    breadCrumb?: BreadCrumb[]
    settings?: ElocusPage
    contentblockIdx?: number
  }>()

  const { lowStimulusActive, largeTextActive } = useBaseAccessibility()

  const useCubeVariant = computed(
    () => props.content.slidesTonenAlsKubus === 'true' && !lowStimulusActive.value && !largeTextActive.value,
  )

  const swiperInstance = ref<SwiperClass | null>(null)
  const slides = ref<HeroSlideItem[]>([])

  function extractEventIds(slidesArray: HeroSlideSource[]): number[] {
    return slidesArray.map((slide) => slide.content.event?._id).filter((id): id is number => !!id)
  }

  function fetchSlidePage(pageId: number) {
    if (!pageId) return Promise.resolve(null)

    return useBaseAsyncData<HeroSlidePageResponse>(`/page/${SLIDES_PAGE_ID}/${pageId}`)
      .then((r) => r.data.value)
      .catch((e) => {
        console.error(e)
        return null
      })
  }

  // an event without programs isn't in the past: only skip events that had programs and all of them have already ended
  function isEventInThePast(event: HeroSlideResolvedEvent): boolean {
    if (!event.programs?.length) return false

    const now = Date.now()
    return event.programs.every((program) => {
      const endTime = new Date(program.endAt || program.startAt).getTime()
      return !Number.isNaN(endTime) && endTime < now
    })
  }

  function resolveSlide(
    slide: HeroSlideSource,
    pagesById: Map<number, HeroSlidePageResponse>,
    eventsById: Map<number, HeroSlideResolvedEvent>,
  ): HeroSlideItem | null {
    const page = pagesById.get(slide._id)
    const resolvedContent = page?.content ?? slide.content
    if (!resolvedContent) return null

    const eventId = resolvedContent.event?._id
    if (!eventId) return { _id: slide._id, content: resolvedContent }

    const resolvedEvent = eventsById.get(eventId)
    if (resolvedEvent == null || isEventInThePast(resolvedEvent)) return null

    return { _id: slide._id, content: resolvedContent, event: resolvedEvent }
  }

  async function resolveSlides(slidesArray: HeroSlideSource[]): Promise<HeroSlideItem[]> {
    const eventIds = extractEventIds(slidesArray)

    const eventOptions = {
      isProgram: false,
      seo: {
        changeHead: false,
        changeUrl: false,
      },
    }
    const { fetchEvents, eventsResponse, setPageSize, setItemsIncluded } = useEventOverview([], eventOptions)
    setPageSize(100)
    setItemsIncluded(eventIds)

    const [, pageResults] = await Promise.all([
      fetchEvents(),
      Promise.all(slidesArray.map((slide) => fetchSlidePage(slide._id))),
    ])

    const resolvedEvents = (eventsResponse.value?.events ?? []) as HeroSlideResolvedEvent[]
    const eventsById = new Map(resolvedEvents.map((event) => [event.id, event]))
    const pagesById = new Map(pageResults.filter((p): p is HeroSlidePageResponse => !!p).map((p) => [p._id, p]))

    return slidesArray
      .map((slide) => resolveSlide(slide, pagesById, eventsById))
      .filter((slide): slide is HeroSlideItem => slide != null)
  }

  if (props.content.slides) {
    slides.value = await resolveSlides(props.content.slides)
  }

  const modules = [Pagination, EffectCreative, Parallax]
  const swiperOptions = {
    modules,
    parallax: true,
    direction: 'horizontal' as const,
    slidesPerView: 1,
    loop: true,
    pagination: { el: '.swiper-pagination', clickable: true },
    breakpoints: {
      1024: { direction: 'vertical' as const },
    },
  }

  function onSwiper(swiper: SwiperClass) {
    swiperInstance.value = swiper

    swiper.on('beforeSlideChangeStart', function () {
      swiper.update()
    })
  }
</script>

<style scoped lang="scss">
  .hero-slider {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 80vh;
    max-height: 80vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .swiper {
    width: 100%;
    height: 100%;
    flex: 1;

    :deep(.swiper-wrapper) {
      height: 100%;

      .swiper-slide {
        height: 100%;
      }
    }
  }

  .swiper-slide {
    display: flex;
  }

  :deep(.swiper-pagination) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--margin-xs);
    right: var(--margin-md);

    .swiper-pagination-bullet {
      width: 1rem;
      height: 1rem;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.4);
      opacity: 1;
      margin: 0 !important;
      transition: all 0.25s ease;
    }

    .swiper-pagination-bullet-active {
      width: 1rem;
      height: 3.4rem;
      border-radius: 4px;
      background: $white;
    }
  }

  @include media(sm) {
    .hero-slider {
      height: auto;
      min-height: 0;
      max-height: 100dvh;
    }

    .swiper {
      height: auto;
      flex: 0 0 auto;

      :deep(.swiper-wrapper) {
        height: auto;

        .swiper-slide {
          height: auto;
          min-height: 80vh;
        }
      }
    }

    :deep(.swiper-pagination) {
      display: flex;
      flex-direction: row;
      justify-content: center;
      bottom: 2rem !important;

      .swiper-pagination-bullet {
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.4);
        opacity: 1;
        margin: 0 !important;
        transition: all 0.25s ease;
      }

      .swiper-pagination-bullet-active {
        width: 1rem;
        height: 1rem;
        border-radius: 4px;
        background: $white;
      }
    }
  }
</style>
